import { useEffect, useState } from 'react'
import { Wifi, WifiOff, RefreshCw, Check, AlertCircle } from 'lucide-react'

/**
 * Sync Status Indicator Component
 * مؤشر حالة المزامنة والاتصال بالإنترنت
 *
 * Features:
 * - Online/Offline detection
 * - Sync status display
 * - Last sync time
 * - Manual sync trigger
 *
 * Protocols Applied:
 * - P-OFFLINE-001: Offline-First Strategy
 * - P-UX-001: User Experience (visual feedback)
 * - P-COMPONENT-001: Clean Component Structure
 */
const SyncStatusIndicator = ({ onSync, lastSyncTime, isSyncing = false }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [showDetails, setShowDetails] = useState(false)
  const [showWarningModal, setShowWarningModal] = useState(false)

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      console.log('[SyncStatus] Network: Online')
    }

    const handleOffline = () => {
      setIsOnline(false)
      console.log('[SyncStatus] Network: Offline')
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Format last sync time
  const formatSyncTime = (timestamp) => {
    if (!timestamp) return 'لم يتم المزامنة بعد'

    const now = new Date()
    const syncDate = new Date(timestamp)
    const diffMs = now - syncDate
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'الآن'
    if (diffMins < 60) return `منذ ${diffMins} دقيقة`
    if (diffHours < 24) return `منذ ${diffHours} ساعة`
    return `منذ ${diffDays} يوم`
  }

  // Handle sync click
  const handleSyncClick = () => {
    if (!isOnline) {
      setShowWarningModal(true)
      return
    }
    if (isSyncing) return
    if (onSync) onSync()
  }

  return (
    <>
      {/* Compact Indicator */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
          isOnline
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
        }`}
        aria-label="حالة المزامنة"
      >
        {isOnline ? (
          <Wifi className="w-4 h-4" />
        ) : (
          <WifiOff className="w-4 h-4" />
        )}
        <span>{isOnline ? 'متصل' : 'غير متصل'}</span>
      </button>

      {/* Details Dropdown */}
      {showDetails && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowDetails(false)}
          />

          {/* Details Card */}
          <div className="absolute left-0 mt-2 w-72 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 animate-fadeIn">
            <div className="p-4 space-y-4">
              {/* Connection Status */}
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  isOnline
                    ? 'bg-green-100 dark:bg-green-900/30'
                    : 'bg-red-100 dark:bg-red-900/30'
                }`}>
                  {isOnline ? (
                    <Wifi className="w-5 h-5 text-green-600 dark:text-green-400" />
                  ) : (
                    <WifiOff className="w-5 h-5 text-red-600 dark:text-red-400" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {isOnline ? 'متصل بالإنترنت' : 'غير متصل'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {isOnline ? 'يمكنك المزامنة الآن' : 'العمل في وضع Offline'}
                  </p>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 dark:border-gray-700" />

              {/* Sync Status */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    آخر مزامنة
                  </span>
                  {isSyncing ? (
                    <div className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                      <RefreshCw className="w-3 h-3 animate-spin" />
                      <span>جاري المزامنة...</span>
                    </div>
                  ) : lastSyncTime ? (
                    <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                      <Check className="w-3 h-3" />
                      <span>{formatSyncTime(lastSyncTime)}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                      <AlertCircle className="w-3 h-3" />
                      <span>لم تتم المزامنة</span>
                    </div>
                  )}
                </div>

                {/* Sync Button */}
                <button
                  onClick={handleSyncClick}
                  disabled={!isOnline || isSyncing}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    !isOnline || isSyncing
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                      : 'bg-primary-600 text-white hover:bg-primary-700 active:scale-95'
                  }`}
                >
                  <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
                  <span>{isSyncing ? 'جاري المزامنة...' : 'مزامنة الآن'}</span>
                </button>
              </div>

              {/* Offline Mode Info */}
              {!isOnline && (
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
                  <div className="flex gap-2">
                    <AlertCircle className="w-4 h-4 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-orange-800 dark:text-orange-300">
                        وضع العمل بدون إنترنت
                      </p>
                      <p className="text-xs text-orange-700 dark:text-orange-400 mt-1">
                        يمكنك إنشاء الفواتير وسيتم حفظها محلياً. سيتم المزامنة عند الاتصال.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Warning Modal - Offline Sync Attempt */}
      {showWarningModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full animate-scaleIn">
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-t-2xl p-6 text-center">
              <div className="bg-white rounded-full w-20 h-20 mx-auto flex items-center justify-center mb-4">
                <WifiOff className="w-10 h-10 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-white">لا يوجد اتصال بالإنترنت</h3>
            </div>

            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 text-lg text-center mb-4">
                لا يمكن المزامنة الآن
              </p>
              <p className="text-gray-600 dark:text-gray-400 text-sm text-center mb-6">
                يرجى التحقق من اتصالك بالإنترنت والمحاولة مرة أخرى
              </p>

              <button
                onClick={() => setShowWarningModal(false)}
                className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold hover:from-orange-600 hover:to-amber-600 transition-all shadow-lg hover:shadow-xl"
              >
                حسناً
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default SyncStatusIndicator
